import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import { translations } from '../translations';

const LANG_STORAGE_KEY = 'l2_calc_lang';

const LanguageContext = createContext(null);

// Map Telegram language codes to supported app languages
function getTelegramLanguage() {
  try {
    const telegramLang = window.Telegram?.WebApp?.initDataUnsafe?.user?.language_code;
    if (!telegramLang) return null;

    // Extract base language code (e.g., 'en-US' -> 'en')
    const baseLang = telegramLang.split('-')[0].toLowerCase();

    // Map to supported languages
    if (baseLang === 'ru') return 'ru';
    if (baseLang === 'uk') return 'ua'; // Telegram uses 'uk' for Ukrainian
    if (baseLang === 'ua') return 'ua';
    if (baseLang === 'en') return 'en';

    return null; // Unsupported language
  } catch (e) {
    return null;
  }
}

export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState(() => {
    try {
      const saved = localStorage.getItem(LANG_STORAGE_KEY);
      if (saved && translations[saved]) return saved;

      // First-time user: try to detect language from Telegram
      const telegramLang = getTelegramLanguage();
      if (telegramLang) {
        // Save detected language so it persists
        localStorage.setItem(LANG_STORAGE_KEY, telegramLang);
        return telegramLang;
      }
    } catch (e) {}
    return 'en';
  });

  const setLang = useCallback((newLang) => {
    if (translations[newLang]) {
      setLangState(newLang);
      try {
        localStorage.setItem(LANG_STORAGE_KEY, newLang);
      } catch (e) {}
    }
  }, []);

  const t = useCallback((key, vars) => {
    let str = translations[lang]?.[key] || translations['en']?.[key] || key;
    if (vars && typeof str === 'string') {
      str = str.replace(/\{(\w+)\}/g, (_, k) => (vars[k] ?? `{${k}}`));
    }
    return str;
  }, [lang]);

  // Slavic pluralization helper (works for Russian, Ukrainian, etc.)
  // Forms: singular (1, 21, 31), few (2-4, 22-24), many (0, 5-20, 25-30)
  const getPlural = useCallback((n, one, few, many) => {
    const lastDigit = n % 10;
    const lastTwoDigits = n % 100;
    
    if (lastTwoDigits >= 11 && lastTwoDigits <= 19) {
      return many;
    }
    if (lastDigit === 1) {
      return one;
    }
    if (lastDigit >= 2 && lastDigit <= 4) {
      return few;
    }
    return many;
  }, []);

  // Memoize context value to prevent unnecessary re-renders
  // Will only change when lang changes (which updates t)
  const value = useMemo(() => ({ 
    lang, 
    setLang, 
    t, 
    getPlural 
  }), [lang, setLang, t, getPlural]);

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }
  return context;
}
